import { getFeatureConfig } from '@luna/feature-config';
import type { Amount } from '@shared/core-types';
import { logger } from '@luna/logger';

import { Amount as AmountClass } from './Amount';
import { DECIMAL_PRECISION_MULTIPLIER } from './types';

const DECIMAL_BASE = 10;
const PRECISION_ZERO = 0;
const DEFAULT_VALUE = 0;
const PRECISION_BASE = 2;
const THOUSAND_GROUPING = 3;
const PERCENT_BASE = 100;

export function applyPrecision(originalValue: number, precision: number): number {
    const decimalPoint = DECIMAL_PRECISION_MULTIPLIER ** precision;
    const withPrecision = (originalValue * decimalPoint).toFixed();
    return Number(withPrecision);
}

export function parsePrecision(originalValue: number, precision: number): number {
    const decimalPoint = DECIMAL_PRECISION_MULTIPLIER ** precision;
    const withPrecision = (originalValue / decimalPoint).toFixed(precision);
    return Number(withPrecision);
}

export function parseAmountPrecision(valueObj?: Amount): string | undefined {
    if (!valueObj) {
        return undefined;
    }

    const decimalPoint = DECIMAL_PRECISION_MULTIPLIER ** valueObj.precision;
    return (valueObj.value / decimalPoint).toFixed(valueObj.precision);
}

export function getValueForPrecision(
    amount: Pick<Amount, 'value' | 'precision'>,
    newPrecision: number,
): number {
    const precisionDiff = newPrecision - amount.precision;

    return parseInt(String(amount.value * DECIMAL_PRECISION_MULTIPLIER ** precisionDiff), 10);
}

export const getAmountWithPrecision = (
    { value, precision, currency }: Amount,
    newPrecision: number,
): Amount => {
    const precisionDiff = newPrecision - precision;

    return {
        currency,
        value: parseInt(String(value * DECIMAL_BASE ** precisionDiff), 10),
        precision: newPrecision,
    };
};

export function sumMoreAmountObjs(
    amountOrArrayOfAmounts?: Amount | (Amount | undefined | null)[],
): number {
    let arrayOfAmount: (Amount | undefined | null)[];

    if (!amountOrArrayOfAmounts) {
        arrayOfAmount = [];
    } else if (Array.isArray(amountOrArrayOfAmounts)) {
        arrayOfAmount = amountOrArrayOfAmounts;
    } else {
        arrayOfAmount = [amountOrArrayOfAmounts];
    }

    return arrayOfAmount.reduce((memo, arrayItem) => {
        if (
            !(arrayItem instanceof Object) ||
            !arrayItem?.value ||
            arrayItem.precision === undefined
        ) {
            return memo;
        }
        const number = arrayItem.value / DECIMAL_PRECISION_MULTIPLIER ** arrayItem.precision;
        return memo + number;
    }, 0);
}

const getHighestPrecision = (amounts: Amount[]): number =>
    Math.max(...amounts.map(({ precision }) => precision));

const hasTheSameCurrency = (amounts: Amount[]): boolean =>
    amounts.every((amount) => amount.currency === amounts[0].currency);

export const sumAmountsToHighestPrecision = (amounts: Amount[]): Amount => {
    if (!hasTheSameCurrency(amounts)) {
        logger.error('cant sum amounts with different currencies');
        throw new Error('cant sum amounts with different currencies');
    }

    const precision = getHighestPrecision(amounts);
    const amountsToSum = amounts.map((amount) => getAmountWithPrecision(amount, precision));

    const value = amountsToSum.reduce((sum, { value: amountValue }) => sum + amountValue, 0);

    return {
        value,
        precision,
        currency: amountsToSum[0].currency,
    };
};

export function getAmount({
    value = DEFAULT_VALUE,
    precision = getFeatureConfig((config) => config.base.defaultPrecision),
    currency = getFeatureConfig((config) => config.base.defaultCurrency),
}: Partial<Amount> = {}): Amount {
    return {
        value,
        precision,
        currency,
    };
}

export function numberToAmount(n: number | undefined, currency = ''): Amount | null {
    if (typeof n !== 'number') {
        return null;
    }

    const precision = String(n).split('.')[1]?.length || PRECISION_ZERO;
    const multiplier = DECIMAL_PRECISION_MULTIPLIER ** precision;
    const value = n * multiplier;

    return { value, precision, currency };
}

export function getInvertedAmount(amount: Amount): Amount {
    return {
        ...amount,
        value: -amount.value,
    };
}

export const getPositiveAmount = (amount?: Amount): Amount | undefined => {
    if (amount?.value) {
        return {
            ...amount,
            value: Math.abs(amount.value),
        };
    }

    return amount;
};

function isOutgoingAmount(amount: Amount): boolean {
    return amount.value < 0;
}

export function getSummedAmountsByCurrencyAndDirection(amounts: Amount[]): Amount[] {
    const incoming = new Map<string, Amount>();
    const outgoing = new Map<string, Amount>();

    amounts.forEach((amount) => {
        const amountsToPerformSearch = isOutgoingAmount(amount) ? outgoing : incoming;

        const matchedAmount = amountsToPerformSearch.get(amount.currency);

        if (matchedAmount) {
            const sumAmount = AmountClass.sum([
                new AmountClass(matchedAmount),
                new AmountClass(amount),
            ]).toJSON();

            amountsToPerformSearch.set(sumAmount.currency, sumAmount);

            return;
        }

        amountsToPerformSearch.set(amount.currency, amount);
    });

    return [...Array.from(outgoing.values()), ...Array.from(incoming.values())];
}

export function getAmountWithCurrency(
    amount: Amount,
    currency: string,
    precision: number = amount.precision,
): Amount {
    const precisionDiff = precision - amount.precision;

    return {
        value: amount.value * DECIMAL_BASE ** precisionDiff,
        precision,
        currency,
    };
}

export function formatMoneyFromNumber(n: number, _c: number, _d: string, _t: string): string {
    let c = Math.abs(_c);

    c = Number.isNaN(c) ? PRECISION_BASE : c;
    const d = _d === undefined ? ',' : _d;
    const t = _t === undefined ? '.' : _t;

    const s = n < 0 ? '-' : '';
    const fixedN = Math.abs(+n || 0).toFixed(c);
    const i = `${parseInt(fixedN, DECIMAL_BASE)}`;
    let j = i.length;
    j = j > THOUSAND_GROUPING ? j % THOUSAND_GROUPING : 0;

    return (
        s +
        (j ? i.substr(0, j) + t : '') +
        i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${t}`) +
        (c
            ? d +
              Math.abs(Number(fixedN) - Number(i))
                  .toFixed(c)
                  .slice(PRECISION_BASE)
            : '')
    );
}

export function formatMoney(
    _val: number,
    roundit?: boolean,
    selPrecision?: number,
    _currencySymbol?: string,
    _isPreCurrency?: boolean,
    useThousandSeparator = true,
): string {
    let isPreCurrency = _isPreCurrency;
    let currencySymbol = _currencySymbol;

    const { base } = getFeatureConfig((c) => c);

    if (isPreCurrency === undefined) {
        isPreCurrency = base.defaultCurrencyPre;
    }

    const val = Number(_val);

    if (currencySymbol === undefined) {
        currencySymbol = '';
    }

    let rstr = '';

    if (isPreCurrency) {
        rstr += `${currencySymbol}`;
    }

    if (roundit) {
        rstr += formatMoneyFromNumber(val, 0, base.decimalSeparator, base.thousandSeparator);
    } else {
        let precision = PRECISION_BASE;
        if (selPrecision !== undefined) precision = selPrecision;
        rstr += formatMoneyFromNumber(
            val,
            precision,
            base.decimalSeparator,
            useThousandSeparator ? base.thousandSeparator : '',
        );
    }

    if (!isPreCurrency && currencySymbol) {
        rstr += ` ${currencySymbol}`;
    }

    return rstr;
}

export function formatAmountObject(
    _val: string | number | Amount,
    roundit?: boolean,
    addCurrency?: boolean,
    useThousandSeparator = true,
): string {
    let val = _val;
    let precisionPow = PRECISION_BASE;
    let preCurrency: string =
        typeof val === 'object' &&
        val.currency &&
        val.currency !== getFeatureConfig((config) => config.base.defaultCurrency)
            ? (val.currency as string)
            : `${getFeatureConfig((config) => config.base.defaultCurrencySign)} `; // "EUR ";
    let isPreCurrency = getFeatureConfig((config) => config.base.defaultCurrencyPre);

    if (typeof val === 'object') {
        if (
            val.currency !== getFeatureConfig((config) => config.base.defaultCurrency) &&
            getFeatureConfig((config) => config.base.doPreCurrencyCheck) &&
            val.currency !== 'STK'
        ) {
            isPreCurrency = true;
            preCurrency = `${val.currency} `;
            // addcurrency = true;
        }
        if (val.currency === 'USD') preCurrency = '$ ';
        if (val.currency === 'EUR') preCurrency = '€ ';

        precisionPow = val.precision as number;
        val = Number(val.value as number);
    } else {
        val = Number(val);
    }

    if (precisionPow !== PRECISION_BASE) {
        val /= DECIMAL_BASE ** precisionPow;
    } else {
        val /= PERCENT_BASE;
    }

    if (addCurrency === false) {
        preCurrency = '';
    }

    return formatMoney(
        val,
        roundit,
        precisionPow,
        preCurrency,
        isPreCurrency,
        useThousandSeparator,
    );
}

export const isEqualAmount = (firstAmount: Amount, secondAmount: Amount) => {
    const precision = Math.max(firstAmount.precision, secondAmount.precision);

    return (
        getValueForPrecision(firstAmount, precision) ===
        getValueForPrecision(secondAmount, precision)
    );
};

export const add = (amount1: Amount, amount2: Amount): Amount =>
    new AmountClass(amount1).add(new AmountClass(amount2)).toJSON();

export const subtract = (amount1: Amount, amount2: Amount): Amount =>
    new AmountClass(amount1).subtract(new AmountClass(amount2)).toJSON();
